import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import SignaturePad from "../components/SignaturePad";
import { Map, Plus, X, Sparkles, PenLine, CheckCircle2, Clock, Lightbulb, Trash2, ChevronDown, ChevronUp, Eraser } from "lucide-react";

const ACCEPTANCE_TEXT = "Confirmo que he recibido la formación descrita en esta sesión y que he revisado el resumen de aprendizaje presentado.";

const STATUS_META = {
  null: { label: "No iniciada", cls: "bg-neutral-100 text-neutral-400" },
  TRABAJADA: { label: "Trabajada", cls: "bg-amber-100 text-amber-800" },
  EN_PROGRESO: { label: "En progreso", cls: "bg-blue-100 text-blue-700" },
  DOMINADA: { label: "Dominada", cls: "bg-green-100 text-green-700" },
};
const CYCLE = [null, "TRABAJADA", "EN_PROGRESO", "DOMINADA"];

function statusMeta(s) { return STATUS_META[s] || STATUS_META[null]; }

export default function CompetencyMap() {
  const { user } = useAuth();
  return (
    <div className="fade-up" data-testid="competency-map-page">
      <h1 className="text-3xl font-semibold tracking-tight mb-1 flex items-center gap-2"><Map className="text-orange-600" /> Mapa de Competencias</h1>
      <p className="text-neutral-500 mb-6">
        {user.role === "ALUMNO"
          ? "Tu cuaderno digital de aprendizaje: qué dominas, qué estás mejorando y tu próximo objetivo."
          : "Ficha única viva por alumno: sesiones, competencias del Método Natur, resúmenes Dakota y firma digital."}
      </p>
      {user.role === "ALUMNO" ? <StudentView /> : <StaffView />}
    </div>
  );
}

/* ---------------------------- Cabecera de progreso ---------------------------- */
function MapHeader({ map, canEditType, onTypeChange }) {
  const s = map.student;
  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-6 mb-6" data-testid="map-header">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-semibold text-lg">{s.name}</p>
          <div className="flex items-center gap-2 mt-1">
            {canEditType ? (
              <select data-testid="student-type-select" value={s.student_type}
                      onChange={(e) => onTypeChange(e.target.value)}
                      className="text-[11px] font-bold rounded-full px-2 py-1 border border-neutral-300 bg-white">
                <option value="NUEVO">ALUMNO NUEVO</option>
                <option value="TRASLADO">ALUMNO TRASLADO</option>
              </select>
            ) : (
              <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full ${s.student_type === "TRASLADO" ? "bg-purple-100 text-purple-700" : "bg-neutral-100 text-neutral-600"}`}>
                {s.student_type === "TRASLADO" ? "ALUMNO TRASLADO" : "ALUMNO NUEVO"}
              </span>
            )}
            {s.student_type === "TRASLADO" && <span className="text-[11px] text-neutral-500">Inicio directo en competencias prácticas</span>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">Preparación práctica</p>
          <p className="text-4xl font-semibold text-orange-600" data-testid="map-progress-percent">{map.progress_percent}%</p>
          <p className="text-xs text-neutral-500">Estado: <strong>{map.state}</strong></p>
        </div>
      </div>
      <div className="mt-4 h-3 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-orange-600 to-orange-400 transition-all" style={{ width: `${map.progress_percent}%` }} />
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-green-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-semibold text-green-700" data-testid="map-dominadas">{map.dominadas}</p>
          <p className="text-[11px] uppercase tracking-wider text-green-700/70">Dominadas</p>
        </div>
        <div className="bg-neutral-50 rounded-xl p-3 text-center">
          <p className="text-2xl font-semibold text-neutral-700" data-testid="map-pendientes">{map.pendientes}</p>
          <p className="text-[11px] uppercase tracking-wider text-neutral-500">Pendientes</p>
        </div>
        <div className="bg-orange-50 rounded-xl p-3 text-center">
          <p className="text-sm font-semibold text-orange-700 mt-1" data-testid="map-next-objective">{map.next_objective}</p>
          <p className="text-[11px] uppercase tracking-wider text-orange-700/70 mt-1">Próximo objetivo</p>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Rejilla de competencias ---------------------------- */
function CategoryGrid({ categories }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="category-grid">
      {categories.map((cat) => (
        <div key={cat.category} className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3">{cat.category}</p>
          <div className="space-y-1.5">
            {cat.competencies.map((c) => {
              const m = statusMeta(c.status);
              return (
                <div key={c.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`competency-row-${c.id}`}>
                  <span className={c.status === "DOMINADA" ? "text-neutral-800" : "text-neutral-600"}>{c.name}
                    {c.times_worked > 0 && <span className="text-[10px] text-neutral-400 ml-1">×{c.times_worked}</span>}
                  </span>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${m.cls}`}>{m.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------- Historial de sesiones ---------------------------- */
function SessionHistory({ sessions, onOpen }) {
  if (!sessions.length) return <p className="text-sm text-neutral-500 bg-white border border-neutral-200/70 rounded-2xl p-6 mb-6">Todavía no hay sesiones registradas.</p>;
  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm divide-y divide-neutral-100 mb-6" data-testid="session-history">
      {sessions.map((s) => (
        <button key={s.id} onClick={() => onOpen(s.id)} data-testid={`session-item-${s.session_number}`}
                className="w-full flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 text-left hover:bg-neutral-50 transition-colors">
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm">Sesión {s.session_number}</span>
            <span className="text-xs text-neutral-500">{fmtDate(s.date)} · {s.zone} · {s.duration_classes} clase{s.duration_classes > 1 ? "s" : ""} · {s.teacher_name}</span>
          </div>
          {s.status === "FIRMADA"
            ? <span className="flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full"><CheckCircle2 size={12} /> Firmada</span>
            : <span className="flex items-center gap-1 text-[11px] font-bold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full"><Clock size={12} /> Pendiente de firma</span>}
        </button>
      ))}
    </div>
  );
}

/* ---------------------------- Detalle de sesión + firma ---------------------------- */
function SessionDetailModal({ sessionId, onClose, onChanged }) {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [dakotaDraft, setDakotaDraft] = useState("");
  const [generating, setGenerating] = useState(false);
  const [signing, setSigning] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const padRef = useRef(null);
  const isStaff = user.role !== "ALUMNO";

  const load = () => api.get(`/competencias/sesiones/${sessionId}`).then((r) => { setS(r.data); setDakotaDraft(r.data.dakota_summary || ""); });
  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line

  if (!s) return null;
  const signed = s.status === "FIRMADA";

  const genDakota = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/competencias/sesiones/${s.id}/dakota`);
      setDakotaDraft(r.data.dakota_summary);
      toast.success("Resumen Dakota generado (sin coste). Revísalo y edítalo antes de la firma.");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setGenerating(false);
  };

  const saveDakota = async () => {
    setSaving(true);
    try {
      await api.patch(`/competencias/sesiones/${s.id}`, { dakota_summary: dakotaDraft });
      toast.success("Resumen guardado");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  const sign = async () => {
    if (!accepted) { toast.error("Debes marcar la casilla de conformidad"); return; }
    if (!padRef.current || padRef.current.isEmpty()) { toast.error("Dibuja tu firma en el recuadro"); return; }
    try {
      await api.post(`/competencias/sesiones/${s.id}/firmar`, { signature_image: padRef.current.toDataURL(), accepted: true });
      toast.success("Sesión firmada correctamente");
      setSigning(false);
      load();
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const byCat = {};
  (s.competencies || []).forEach((c) => { (byCat[c.category] = byCat[c.category] || []).push(c); });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="session-detail-modal">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="font-semibold text-lg">Sesión {s.session_number} · {s.student_name}</p>
            <p className="text-sm text-neutral-500">{fmtDate(s.date)} · {s.start_time}–{s.end_time} · {s.duration_classes} clase{s.duration_classes > 1 ? "s" : ""} · Profesor: {s.teacher_name}</p>
            <p className="text-sm text-neutral-500">Zona de trabajo: <strong className="text-neutral-700">{s.zone}</strong></p>
          </div>
          <button onClick={onClose} data-testid="session-detail-close" className="text-neutral-400 hover:text-neutral-700"><X size={20} /></button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Competencias trabajadas</p>
            {Object.entries(byCat).map(([cat, items]) => (
              <div key={cat} className="mb-2">
                <p className="text-[11px] text-neutral-400 uppercase">{cat}</p>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {items.map((c) => {
                    const m = statusMeta(c.status);
                    return <span key={c.competency_id} className={`text-xs px-2.5 py-1 rounded-full font-medium ${m.cls}`}>{c.name} · {m.label}</span>;
                  })}
                </div>
              </div>
            ))}
          </div>

          {s.teacher_observations && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Observaciones del profesor</p>
              <p className="text-sm bg-neutral-50 rounded-xl p-3 whitespace-pre-wrap">{s.teacher_observations}</p>
            </div>
          )}

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1 flex items-center gap-1"><Sparkles size={13} className="text-orange-600" /> Resumen de aprendizaje (Dakota)</p>
            {isStaff && !signed ? (
              <>
                <Textarea data-testid="dakota-summary-textarea" rows={8} value={dakotaDraft} onChange={(e) => setDakotaDraft(e.target.value)}
                          placeholder="Genera el resumen con Dakota y edítalo antes de presentarlo al alumno." />
                <div className="flex gap-2 mt-2">
                  <Button data-testid="generate-dakota-btn" size="sm" variant="outline" onClick={genDakota} disabled={generating}>
                    <Sparkles size={14} className="mr-1" /> {generating ? "Generando…" : (s.dakota_summary ? "Regenerar con Dakota" : "Generar resumen Dakota")}
                  </Button>
                  <Button data-testid="save-dakota-btn" size="sm" onClick={saveDakota} disabled={saving || dakotaDraft === (s.dakota_summary || "")}
                          className="bg-orange-600 hover:bg-orange-700">Guardar resumen</Button>
                </div>
              </>
            ) : (
              <p className="text-sm bg-orange-50/60 border border-orange-100 rounded-xl p-3 whitespace-pre-wrap" data-testid="dakota-summary-readonly">
                {s.dakota_summary || "El profesor todavía está preparando el resumen de esta sesión."}
              </p>
            )}
          </div>

          {signed ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4" data-testid="signature-block">
              <p className="text-xs font-bold uppercase tracking-wider text-green-700 mb-2 flex items-center gap-1"><CheckCircle2 size={13} /> Firmada digitalmente</p>
              <img src={s.signature.image} alt="Firma del alumno" className="h-20 bg-white rounded-lg border border-green-200" />
              <p className="text-xs text-neutral-600 mt-2">"{s.signature.accepted_text}"</p>
              <p className="text-[11px] text-neutral-500 mt-1">
                Firmado el {new Date(s.signature.signed_at).toLocaleString("es-ES")} · vía {s.signature.via === "ALUMNO" ? "cuenta del alumno" : "dispositivo del profesor"}
              </p>
            </div>
          ) : signing ? (
            <div className="border-2 border-orange-200 rounded-xl p-4 bg-orange-50/40" data-testid="sign-panel">
              <p className="text-sm font-semibold mb-2">Firma del alumno</p>
              <SignaturePad ref={padRef} />
              <div className="flex justify-end mt-1">
                <button onClick={() => padRef.current?.clear()} className="text-xs text-neutral-500 hover:text-neutral-800 flex items-center gap-1"><Eraser size={12} /> Borrar firma</button>
              </div>
              <label className="flex items-start gap-2 mt-2 text-sm cursor-pointer">
                <input type="checkbox" data-testid="acceptance-checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-1 accent-orange-600" />
                <span className="text-neutral-700">{ACCEPTANCE_TEXT}</span>
              </label>
              <div className="flex gap-2 mt-3">
                <Button data-testid="confirm-sign-btn" onClick={sign} className="bg-orange-600 hover:bg-orange-700"><PenLine size={14} className="mr-1" /> Firmar sesión</Button>
                <Button variant="ghost" onClick={() => setSigning(false)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(s.dakota_summary || "").trim() ? (
                <Button data-testid="open-sign-btn" onClick={() => setSigning(true)} className="bg-orange-600 hover:bg-orange-700">
                  <PenLine size={14} className="mr-1" /> {isStaff ? "Firmar ahora (alumno firma en este dispositivo)" : "Revisar y firmar"}
                </Button>
              ) : (
                !isStaff && <p className="text-xs text-neutral-500">Podrás firmar cuando tu profesor cierre el resumen de la sesión.</p>
              )}
              {isStaff && (
                <Button data-testid="delete-session-btn" variant="ghost" className="text-red-500 hover:text-red-700" onClick={async () => {
                  const pin = window.prompt(`PIN Maestro para ELIMINAR la Sesión ${s.session_number} de ${s.student_name}:`);
                  if (!pin) return;
                  try {
                    await api.delete(`/competencias/sesiones/${s.id}`, { headers: { "X-Master-Pin": pin } });
                    toast.success("Sesión eliminada"); onChanged(); onClose();
                  } catch (e) { toast.error(errMsg(e)); }
                }}><Trash2 size={14} className="mr-1" /> Eliminar</Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Nueva sesión (staff) ---------------------------- */
function NewSessionModal({ studentId, onClose, onCreated }) {
  const [catalog, setCatalog] = useState([]);
  const [zones, setZones] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10), start_time: "10:00", end_time: "11:30",
    duration_classes: 1, zone: "", teacher_observations: "",
  });

  useEffect(() => {
    api.get("/competencias/catalogo").then((r) => setCatalog(r.data));
    api.get("/lov/ZONAS_TRABAJO").then((r) => setZones(r.data.values || []));
  }, []);

  const byCat = useMemo(() => {
    const g = {};
    catalog.forEach((c) => { (g[c.category] = g[c.category] || []).push(c); });
    return g;
  }, [catalog]);

  const cycle = (id) => setStatuses((st) => {
    const next = CYCLE[(CYCLE.indexOf(st[id] || null) + 1) % CYCLE.length];
    const copy = { ...st };
    if (next === null) delete copy[id]; else copy[id] = next;
    return copy;
  });

  const submit = async () => {
    const comps = Object.entries(statuses).map(([competency_id, status]) => ({ competency_id, status }));
    if (!comps.length) { toast.error("Marca al menos una competencia trabajada"); return; }
    if (!form.zone) { toast.error("Selecciona la zona de trabajo"); return; }
    setSaving(true);
    try {
      const r = await api.post("/competencias/sesiones", { student_id: studentId, ...form, duration_classes: Number(form.duration_classes) || 1, competencies: comps });
      toast.success(`Sesión ${r.data.session_number} registrada. Genera el resumen Dakota antes de la firma.`);
      onCreated(r.data.id);
    } catch (e) { toast.error(errMsg(e)); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="new-session-modal">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-lg">Nueva sesión</p>
          <button onClick={onClose} data-testid="new-session-close" className="text-neutral-400 hover:text-neutral-700"><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="text-xs text-neutral-500 block mb-1">Fecha</label>
            <Input data-testid="session-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-neutral-500 block mb-1">Hora inicio</label>
            <Input data-testid="session-start-input" type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-neutral-500 block mb-1">Hora fin</label>
            <Input data-testid="session-end-input" type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-neutral-500 block mb-1">Nº de clases</label>
            <Input data-testid="session-classes-input" type="number" min={1} value={form.duration_classes}
                   onChange={(e) => setForm({ ...form, duration_classes: e.target.value })} />
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-neutral-500 block mb-1">Zona de trabajo</label>
          <div className="flex gap-2">
            <select data-testid="session-zone-select" value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })}
                    className="h-10 flex-1 rounded-md border border-neutral-300 px-3 text-sm bg-white">
              <option value="">— Selecciona zona —</option>
              {zones.map((z) => <option key={z.id} value={z.label}>{z.label}</option>)}
            </select>
            <button type="button" title="Añadir zona" data-testid="session-zone-add-btn"
                    onClick={async () => {
                      const label = window.prompt("Nueva zona de trabajo:"); if (!label) return;
                      const pin = window.prompt("PIN Maestro:"); if (!pin) return;
                      try {
                        await api.post("/lov/ZONAS_TRABAJO", { label }, { headers: { "X-Master-Pin": pin } });
                        const r = await api.get("/lov/ZONAS_TRABAJO");
                        setZones(r.data.values || []);
                        setForm((f) => ({ ...f, zone: label }));
                        toast.success("Zona añadida al catálogo");
                      } catch (e) { toast.error(errMsg(e)); }
                    }}
                    className="h-10 w-10 rounded-md border border-neutral-300 text-neutral-500 hover:text-orange-600 hover:border-orange-400"><Plus size={16} className="mx-auto" /></button>
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-neutral-500 block mb-2">Competencias trabajadas <span className="text-neutral-400">(toca para cambiar: Trabajada → En progreso → Dominada)</span></label>
          <div className="space-y-3 max-h-72 overflow-y-auto border border-neutral-100 rounded-xl p-3">
            {Object.entries(byCat).map(([cat, items]) => (
              <div key={cat}>
                <p className="text-[11px] text-neutral-400 uppercase mb-1">{cat}</p>
                <div className="flex flex-wrap gap-1.5">
                  {items.map((c) => {
                    const st = statuses[c.id] || null;
                    const m = statusMeta(st);
                    return (
                      <button key={c.id} type="button" onClick={() => cycle(c.id)} data-testid={`comp-toggle-${c.id}`}
                              className={`text-xs px-2.5 py-1.5 rounded-full font-medium border transition-colors ${st ? `${m.cls} border-transparent` : "bg-white text-neutral-500 border-neutral-200 hover:border-orange-300"}`}>
                        {c.name}{st ? ` · ${m.label}` : ""}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <label className="text-xs text-neutral-500 block mb-1">Observaciones del profesor</label>
          <Textarea data-testid="session-observations-input" rows={3} value={form.teacher_observations}
                    onChange={(e) => setForm({ ...form, teacher_observations: e.target.value })}
                    placeholder="Ej.: Se observa mejora en observación y posicionamiento. Reforzar la segunda detención en doble STOP." />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button data-testid="create-session-btn" onClick={submit} disabled={saving} className="bg-orange-600 hover:bg-orange-700">
            {saving ? "Guardando…" : "Registrar sesión"}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Propuestas de mejora ---------------------------- */
function ProposalsPanel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [form, setForm] = useState({ title: "", category: "", description: "" });
  const load = () => api.get("/competencias/propuestas").then((r) => setProposals(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Escribe el título de la propuesta"); return; }
    try {
      await api.post("/competencias/propuestas", form);
      toast.success("Propuesta enviada a Matriz Natur");
      setForm({ title: "", category: "", description: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resolve = async (p, action) => {
    const pin = window.prompt(`PIN Maestro para ${action === "aprobar" ? "APROBAR" : "RECHAZAR"} "${p.title}":`);
    if (!pin) return;
    try {
      await api.post(`/competencias/propuestas/${p.id}/${action}`, {}, { headers: { "X-Master-Pin": pin } });
      toast.success(action === "aprobar" ? "Propuesta aprobada: nueva competencia incorporada al Método Natur para toda la red" : "Propuesta rechazada");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const PSTATUS = { PENDIENTE: "bg-amber-100 text-amber-800", APROBADA: "bg-green-100 text-green-700", RECHAZADA: "bg-red-100 text-red-700" };

  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm mb-6" data-testid="proposals-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4">
        <span className="font-semibold flex items-center gap-2 text-sm"><Lightbulb size={16} className="text-orange-600" /> Propuestas de mejora del Método Natur
          {proposals.filter((p) => p.status === "PENDIENTE").length > 0 && user.role === "SUPER_ADMIN" &&
            <span className="text-[10px] font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">{proposals.filter((p) => p.status === "PENDIENTE").length} pendientes</span>}
        </span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          {user.role !== "FRANQUICIA" && (
            <div className="bg-neutral-50 rounded-xl p-4 mb-4">
              <p className="text-xs text-neutral-500 mb-2">Detectas una carencia formativa? Propón una nueva competencia. Solo Matriz decide; si se aprueba, pasa al Método Natur de toda la red.</p>
              <div className="grid sm:grid-cols-2 gap-2 mb-2">
                <Input data-testid="proposal-title-input" placeholder="Título (ej.: Nueva tipología de doble STOP)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                <Input data-testid="proposal-category-input" placeholder="Categoría sugerida (ej.: CIRCULACIÓN)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              </div>
              <Textarea data-testid="proposal-description-input" rows={2} placeholder="Justificación (ej.: alto índice de suspensos detectado en…)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <Button data-testid="proposal-send-btn" size="sm" onClick={create} className="mt-2 bg-orange-600 hover:bg-orange-700">Enviar a Matriz</Button>
            </div>
          )}
          {user.role === "FRANQUICIA" && <p className="text-xs text-neutral-500 mb-3">Como franquicia puedes visualizar las propuestas de tus profesores. La decisión corresponde exclusivamente a Matriz Natur.</p>}
          <div className="space-y-2">
            {proposals.map((p) => (
              <div key={p.id} className="border border-neutral-100 rounded-xl p-3 text-sm" data-testid={`proposal-${p.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium">{p.title} {p.category && <span className="text-[10px] text-neutral-400 uppercase ml-1">{p.category}</span>}</p>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${PSTATUS[p.status]}`}>{p.status}</span>
                </div>
                <p className="text-xs text-neutral-500 mt-1">{p.description}</p>
                <p className="text-[11px] text-neutral-400 mt-1">{p.author_name} · {fmtDate(p.created_at)}{p.resolved_by ? ` · Resuelta por ${p.resolved_by}` : ""}</p>
                {user.role === "SUPER_ADMIN" && p.status === "PENDIENTE" && (
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" variant="outline" data-testid={`proposal-approve-${p.id}`} className="text-green-700 border-green-300 hover:bg-green-50" onClick={() => resolve(p, "aprobar")}>Aprobar (PIN)</Button>
                    <Button size="sm" variant="outline" data-testid={`proposal-reject-${p.id}`} className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => resolve(p, "rechazar")}>Rechazar (PIN)</Button>
                  </div>
                )}
              </div>
            ))}
            {proposals.length === 0 && <p className="text-xs text-neutral-400">Sin propuestas por ahora.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Catálogo Método Natur (solo Matriz) ---------------------------- */
function CatalogAdmin({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [form, setForm] = useState({ category: "FUNDAMENTOS", name: "" });
  const load = () => api.get("/competencias/catalogo").then((r) => setCatalog(r.data));
  useEffect(() => { load(); }, []);
  const cats = [...new Set(catalog.map((c) => c.category))];

  const add = async () => {
    if (!form.name.trim()) { toast.error("Escribe el nombre de la competencia"); return; }
    const pin = window.prompt("PIN Maestro:"); if (!pin) return;
    try {
      await api.post("/competencias/catalogo", form, { headers: { "X-Master-Pin": pin } });
      toast.success("Competencia añadida al Método Natur");
      setForm({ ...form, name: "" }); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (c) => {
    const pin = window.prompt(`PIN Maestro para eliminar "${c.name}" del Método Natur:`); if (!pin) return;
    try {
      await api.delete(`/competencias/catalogo/${c.id}`, { headers: { "X-Master-Pin": pin } });
      toast.success("Competencia eliminada"); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rename = async (c) => {
    const name = window.prompt("Nuevo nombre:", c.name); if (!name || name === c.name) return;
    const pin = window.prompt("PIN Maestro:"); if (!pin) return;
    try {
      await api.patch(`/competencias/catalogo/${c.id}`, { name }, { headers: { "X-Master-Pin": pin } });
      toast.success("Competencia renombrada"); load(); onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="bg-white border border-neutral-200/70 rounded-2xl shadow-sm mb-6" data-testid="catalog-admin">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4">
        <span className="font-semibold text-sm">⚙️ Catálogo del Método Natur <span className="text-neutral-400 font-normal">(solo Matriz · {catalog.length} competencias)</span></span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-5 pb-5">
          <div className="flex flex-wrap gap-2 mb-4">
            <select data-testid="catalog-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="h-9 rounded-md border border-neutral-300 px-2 text-sm bg-white">
              {["FUNDAMENTOS", "OBSERVACIÓN", "CIRCULACIÓN", "ESTACIONAMIENTOS", "EXAMEN", ...cats.filter((c) => !["FUNDAMENTOS", "OBSERVACIÓN", "CIRCULACIÓN", "ESTACIONAMIENTOS", "EXAMEN"].includes(c))].map((c) => <option key={c}>{c}</option>)}
            </select>
            <Input data-testid="catalog-name-input" className="h-9 flex-1 min-w-[180px]" placeholder="Nueva competencia…" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Button data-testid="catalog-add-btn" size="sm" onClick={add} className="bg-orange-600 hover:bg-orange-700"><Plus size={14} className="mr-1" /> Añadir (PIN)</Button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {catalog.map((c) => (
              <div key={c.id} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg hover:bg-neutral-50">
                <span><span className="text-[10px] text-neutral-400 uppercase mr-2">{c.category}</span>{c.name}</span>
                <span className="flex gap-2">
                  <button onClick={() => rename(c)} className="text-neutral-400 hover:text-orange-600 text-xs">Renombrar</button>
                  <button onClick={() => del(c)} className="text-neutral-400 hover:text-red-600"><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------- Vista STAFF ---------------------------- */
function StaffView() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [map, setMap] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => { api.get("/users").then((r) => setStudents(r.data.filter((u) => u.role === "ALUMNO"))); }, []);

  const loadMap = () => {
    if (!studentId) { setMap(null); return; }
    api.get(`/competencias/mapa/${studentId}`).then((r) => setMap(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { loadMap(); }, [studentId]); // eslint-disable-line

  const changeType = async (t) => {
    try {
      await api.patch(`/competencias/alumno/${studentId}/tipo`, { student_type: t });
      toast.success(t === "TRASLADO" ? "Alumno marcado como TRASLADO: puede iniciar prácticas inmediatamente" : "Alumno marcado como NUEVO");
      loadMap();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select data-testid="map-student-select" value={studentId} onChange={(e) => setStudentId(e.target.value)}
                className="h-10 rounded-md border border-neutral-300 px-3 text-sm bg-white min-w-[240px]">
          <option value="">— Selecciona un alumno —</option>
          {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {studentId && (
          <Button data-testid="new-session-btn" onClick={() => setShowNew(true)} className="bg-orange-600 hover:bg-orange-700">
            <Plus size={15} className="mr-1" /> Nueva sesión
          </Button>
        )}
      </div>

      {user.role === "SUPER_ADMIN" && <CatalogAdmin onChanged={loadMap} />}
      <ProposalsPanel />

      {!studentId && <p className="text-sm text-neutral-500 bg-white border border-neutral-200/70 rounded-2xl p-6">Selecciona un alumno para ver su Mapa de Competencias: una única ficha viva con todas sus sesiones, su progreso y sus firmas.</p>}

      {map && (
        <>
          <MapHeader map={map} canEditType onTypeChange={changeType} />
          {map.pending_signature > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 mb-4" data-testid="pending-signature-alert">
              ⏳ {map.pending_signature} sesión(es) pendiente(s) de firma del alumno.
            </div>
          )}
          <CategoryGrid categories={map.categories} />
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Historial de sesiones</p>
          <SessionHistory sessions={map.sessions} onOpen={setDetailId} />
        </>
      )}

      {showNew && studentId && (
        <NewSessionModal studentId={studentId} onClose={() => setShowNew(false)}
                         onCreated={(sid) => { setShowNew(false); loadMap(); setDetailId(sid); }} />
      )}
      {detailId && <SessionDetailModal sessionId={detailId} onClose={() => setDetailId(null)} onChanged={loadMap} />}
    </div>
  );
}

/* ---------------------------- Vista ALUMNO ---------------------------- */
function StudentView() {
  const [map, setMap] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const loadMap = () => api.get("/competencias/mapa/me").then((r) => setMap(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { loadMap(); }, []); // eslint-disable-line

  if (!map) return <p className="text-neutral-500">Cargando…</p>;
  return (
    <div>
      <MapHeader map={map} canEditType={false} />
      {map.pending_signature > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 mb-4" data-testid="student-pending-alert">
          ✍️ Tienes {map.pending_signature} sesión(es) pendiente(s) de revisar y firmar. Ábrela desde el historial.
        </div>
      )}
      <CategoryGrid categories={map.categories} />
      <p className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2">Historial de sesiones</p>
      <SessionHistory sessions={map.sessions} onOpen={setDetailId} />
      {detailId && <SessionDetailModal sessionId={detailId} onClose={() => setDetailId(null)} onChanged={loadMap} />}
    </div>
  );
}
